import { BaseComponent } from 'components/BaseComponent';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import { Button } from '../../../../../components/Buttons';
import { Checkbox } from '../../../../../components/Checkbox';
import { Dropdown } from '../../../../../components/Dropdown';
import { ManualDropdown } from '../../../../../components/ManualDropdown';
import { FormField } from '../../../../../components/FormFields';
import { DecoratorLabel, Input } from '../../../../../components/Input';
import { Loader } from '../../../../../components/Loader';
import {
  calculatePurchasePricesBTW,
  calculatePurchasePricesMARGE,
  deleteVehicleProperty,
  initiateNewVehicleValue,
  setNewVehicleValue,
} from '../../../actions';
import { selectNewVehicle, selectNewVehicleValue, selectVehicle } from '../../../selectors';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { VehicleCreateFieldCompany } from '../FieldCompany';
import { VehicleCreateFieldCompanyUser } from '../FieldCompanyUser';
import { selectConfig, selectWork } from '../../../../Api/selectors';
import {REQUEST_VEHICLE_PROPERTY_UPDATE, VEHICLE_PURPOSE, VEHICLE_TAX_TYPE} from '../../../constants';
import { BtwOrMarge } from '../../../../Invoices/constants';
import { fixPriceFormat, getQuantOfDecimalPlaces } from '../../../../../utils/string';
import { DROPDOWN_OPTIONS } from '../../../components/VehiclePropertyEditableInput';
import _ from 'lodash';
import env from '../../../../../env';
import { AppClientId } from '../../../../Shared/constants';
import { Times, CircleTimes } from '../../../../../components/Icon';
import cn from 'classnames';
import CheckboxComponent from '../../../../../containers/App/Components/Checkbox/Checkbox';
import ButtonComponent from '../../../../../containers/App/Components/Button/Button';
import styles from './Fields.module.css';

dayjs.extend(customParseFormat);

const MANUAL_DROPDOWN_OPTIONS = {
  tax_type: ['BTW', 'marge'],
  vehicle_purpose: [
    'passenger',
    'commercial',
    'Trekker',
    'Bakwagen',
    'Oplegger',
    'Aanhangwagen',
    'Landbouw- of bosbouwtrekker',
    'Motorfiets' ,
    'Bromfiets',
    'Klein materieel (binnenopslag) per m2',
    'Klein/middelgroot materieel buitenopslag <6m2',
    'Groot & afwijkend materieel buitenopslag >6m2',
    'chassiscabine_less_3500',
    'chassiscabine_greater_3500',
    'motor',
    'oplegger',
    'aanhanger',
  ],
};

const FINANCIAL_TYPES = [
  'financial_purchase_price_incl_vat_bpm',
  'financial_purchase_price_incl_vat_excl_bpm',
  'financial_purchase_price_excl_vat_excl_bpm',
  'vat_percentage',
  'vat_amount',
  'rest_bpm',
  'financial_bpm_gross',
  'economic_prize',
  'financial_price_gross_incl_btw_bpm',
  'financial_price_b2b_incl_vat_btpm',
  'financial_price_nett_excl_vat_bpm',
  'financial_purchase_price_marge_excl_bpm',
  'financial_purchase_price_marge_bpm',
  'financial_price_b2c',
];

const Container = styled.div`
  margin-bottom: 8px;
  .formField {
    .label {
      text-transform: capitalize;
      margin-bottom: 6px;
      color: #6c757d;
    }
  }
`;

const STATE = {
  value: '',
};

/* eslint-disable react/prefer-stateless-function */
class VehicleCreateFieldComp extends BaseComponent {
  constructor(props) {
    super(props, { ...STATE });
  }

  componentDidMount() {
    this.props.initiateValue();

    if (FINANCIAL_TYPES.includes(this.props.type)) {
      const value = this.props.value;
      value ? this.props.setValue(fixPriceFormat(value)) : null;
    }
  }

  get isWientjes() {
    return (
      env.clientId === AppClientId.AUTOWIENTJES ||
      env.clientId === AppClientId.HARRY_NIJBOER ||
      AppClientId.INDEWAL ||
      AppClientId.IDWLEASE ||
      AppClientId.AUTOHUISSALLAND
    );
  }

  isCheckbox() {
    return ['financial_purchase_invoice_paid'].indexOf(this.props.type) > -1;
  }

  isDropbox() {
    return this.props.type in DROPDOWN_OPTIONS;
  }

  isManualDropbox() {
    return this.props.type in MANUAL_DROPDOWN_OPTIONS;
  }

  manualDropdown() {
    const vals = MANUAL_DROPDOWN_OPTIONS[this.props.type];
    return (
      <ManualDropdown
        model={'vehicle'}
        key={this.props.type}
        type={this.props.type}
        editable={false}
        deletable={this.props.vehicle && this.props.vehicle[this.props.type]}
        disabled={this.props.vehicle?.incoming_invoice}
        options={vals}
        onChange={value => this.props.setValue(value)}
        onReset={() => this.props.setValue('')}
        onDeleteValue={value =>
          this.props.deleteProperty(this.props.values?.id, this.props.type, result => {
            if (result === true) {
              this.props.setValue('');
            }
          })
        }
        onManual={value => {
          return this.props.setValue(value, this.props.type + '_manual');
        }}
      />
    );
  }

  wrap(children) {
    const containsCheckbox = element => {
      if (React.isValidElement(element) && element.props) {
        return typeof element.props.className === 'string' && element.props.className.includes('checkbox');
      }
      return false;
    };

    const isCheckboxComp = React.Children.toArray(children).some(child => containsCheckbox(child));

    if (isCheckboxComp) {
      return <div>{children}</div>;
    } else {
      return (
        <div>
          <label className="form-label">{this.formFieldLabel()}</label>
          {children}
        </div>
      );
    }
  }

  formFieldLabel() {
    if (this.isCheckbox()) return null;
    return this.lbl(`vehicle.property.${this.adjustPropTypeLabel(this.props.type)}`);
  }

  isCommercialVehicle2025() {
    const isCommercial = this.props.values?.vehicle_purpose === VEHICLE_PURPOSE.Commercial;
    const dateFirstAllowance = this.props.values?.date_first_allowance;
    const is2025OrLater = dateFirstAllowance ? new Date(dateFirstAllowance).getFullYear() >= 2025 : true;
    console.log('isCommercial', isCommercial);
    console.log('is2025OrLater', is2025OrLater);

    return isCommercial && is2025OrLater;
  }

  adjustPropTypeLabel(type) {
    if (type === 'financial_price_b2c' && this.isCommercialVehicle2025()) return 'financial_price_b2c_commercial_2025';
    if (type === 'financial_price_b2b_incl_vat_btpm' && this.isCommercialVehicle2025()) return 'financial_price_b2b_incl_vat_btpm_commercial_2025';

    return type;
  }

  dropdown() {
    const vals = DROPDOWN_OPTIONS[this.props.type];

    const options = vals.map(val => ({
      label: this.lbl(`vehicle.property.${this.props.type}.${val}`),
      value: val,
    }));

    const value = options.find(o => o.value == this.props.value);
    return (
      <div className={styles.container}>
        <Dropdown
          className={styles.dropdown}
          options={options}
          value={value}
          onChange={pair => this.props.setValue(pair.value)}
        />
      </div>
    );
  }

  salesPriceDecorator() {
    const deleteProperty = () =>
      this.props.deleteProperty(this.props.values?.id, this.props.type, result => {
        if (result.length) {
          this.props.setValue('', this.props.type);
        }
      });
    return this.isWientjes ? (
      <>
        <ButtonComponent style="ButtonSoftPrimary" onClick={() => this.onChangeSalesPrice(this.props.type)}>
          Calc
        </ButtonComponent>
        <ButtonComponent style="ButtonSoftDelete" disabled={this.props.disabled} onClick={() => deleteProperty()}>
          <CircleTimes />
        </ButtonComponent>
      </>
    ) : (
      <>
        <div className={styles.decoratorLabel}>€</div>
        <ButtonComponent style="ButtonSoftDelete" disabled={this.props.disabled} onClick={() => deleteProperty()}>
          <CircleTimes />
        </ButtonComponent>
      </>
    );
  }

  reset() {
    this.setState({ ...STATE });
  }

  onChangePriceWithRounding(enteredValue) {
    this.props.saveDisable(true);
    const value = enteredValue || '';
    if (getQuantOfDecimalPlaces(value) > 2) return;
    this.props.setValue(value);
    if (value) {
      this._t = this.timeout(() => this.onSetFixedValue(value), 2500, this._t);
    }
    return;
  }

  onSetFixedValue(value) {
    const roundValue = value ? parseFloat(value?.replace(',', '.')) : value;
    this.props.setValue(fixPriceFormat(roundValue));

    this.props.saveDisable(false);
  }

  onChangeInputPurchasePrice(evt, type) {
    if (getQuantOfDecimalPlaces(evt.target.value) > 2) return;
    this.props.saveDisable(true);
    this.props.setValue(evt.target.value);
    this._t = this.timeout(() => this.calculatePurchasePrices(type), 3500, this._t);
  }

  calculatePurchasePrices(type) {
    let enteredAmount = this.props[type];
    let amount = this.sanitizePrice(enteredAmount)
    if (this.props.taxType === BtwOrMarge.BTW) {
      // Besides rest_bpm should be passed one of the vehicle prices
      if (type === 'rest_bpm') {
        const btwPriceTypes = [
          'financial_purchase_price_excl_vat_excl_bpm',
          'financial_purchase_price_incl_vat_excl_bpm',
          'financial_purchase_price_incl_vat_bpm',
          'financial_purchase_price_incl_bpm_excl_btw',
        ];
        type = btwPriceTypes.find(currentType => !!this.sanitizePrice(this.props[currentType])) ?? 'financial_purchase_price_excl_vat_excl_bpm';
        amount = this.sanitizePrice(this.props[type] ?? 0);

      }

      this.props.calculatePurchasePricesBTW(
        {
          [type]: amount,
          rest_bpm:
            typeof this.props.rest_bpm == 'string'
              ? parseFloat(this.props.rest_bpm?.replace(',', '.'))
              : _.toNumber(this.props.rest_bpm),
        },
        this.props.vehicle_purpose,
      );
    }

    if (this.props.taxType === BtwOrMarge.MARGE) {
      // Besides rest_bpm should be passed one of the vehicle prices
      if (type === 'rest_bpm') {
        const btwPriceTypes = [
          'financial_purchase_price_marge_bpm',
          'financial_purchase_price_marge_excl_bpm',
        ];
        type = btwPriceTypes.find(currentType => !!this.sanitizePrice(this.props[currentType])) ?? 'financial_purchase_price_marge_excl_bpm';
        amount = this.sanitizePrice(this.props[type] ?? 0);

      }

      this.props.calculatePurchasePricesMARGE(
        {
          [type]: amount,
          rest_bpm:
            typeof this.props.rest_bpm == 'string'
              ? parseFloat(this.props.rest_bpm?.replace(',', '.'))
              : _.toNumber(this.props.rest_bpm),
        },
        this.props.vehicle_purpose,
      );
    }

    this.props.saveDisable(false);
  }

  sanitizePrice(enteredAmount) {
    return parseFloat(enteredAmount?.replace(',', '.'));
  }

  // WIENTJES_INSTALATION_CALCS

  onChangeSalesPrice(type) {
    this._t = this.timeout(this.calculateSalesPrice.bind(this), 300, this._t);
  }

  calculateSalesPrice() {
    const values = this.props.values;
    const type = this.props.type;
    const taxType = this.props.values?.tax_type;
    const vehiclePurpose = this.props.values?.vehicle_purpose;
    const isNew = this.props.values?.new_from_factory == '1';
    const isImport = this.props.values?.import;

    let financial_price_b2b_incl_vat_btpm = this.props.values?.financial_price_b2b_incl_vat_btpm;
    let financial_price_gross_incl_btw_bpm = this.props.values?.financial_price_gross_incl_btw_bpm;
    let financial_price_nett_excl_vat_bpm = this.props.values?.financial_price_nett_excl_vat_bpm;

    const taxYear = parseInt(this.props.values?.tax_year, 10);
    let restBPMApplicableForCommercial = isNaN(taxYear) || taxYear >= 2025;

    // SCENARIO 1;

    if (taxType == BtwOrMarge.BTW && vehiclePurpose == 'passenger' && !isNew) {
      if (type == 'financial_price_b2b_incl_vat_btpm') {
        financial_price_gross_incl_btw_bpm = (+this.props.value || 0) - (+values?.outgoing_rest_bpm || 0);
        financial_price_nett_excl_vat_bpm = financial_price_gross_incl_btw_bpm / 1.21;
      }

      if (type == 'financial_price_gross_incl_btw_bpm') {
        financial_price_nett_excl_vat_bpm = (+this.props.value || 0) / 1.21;
        financial_price_b2b_incl_vat_btpm =
          (+financial_price_nett_excl_vat_bpm || 0) * 1.21 + +values?.outgoing_rest_bpm || 0;
      }

      if (type == 'financial_price_nett_excl_vat_bpm') {
        financial_price_b2b_incl_vat_btpm = (+this.props.value || 0) * 1.21 + +values?.outgoing_rest_bpm || 0;
        financial_price_gross_incl_btw_bpm = (+financial_price_b2b_incl_vat_btpm || 0) - (+values?.outgoing_rest_bpm || 0);
      }
    }

    // SCENARIO 2;

    if (taxType == BtwOrMarge.BTW && vehiclePurpose == 'commercial' && !restBPMApplicableForCommercial) {
      if (type == 'financial_price_b2b_incl_vat_btpm') {
        financial_price_gross_incl_btw_bpm = (+this.props.value || 0) * 1.21;
        financial_price_nett_excl_vat_bpm = (+financial_price_gross_incl_btw_bpm || 0) / 1.21;
      }

      if (type == 'financial_price_gross_incl_btw_bpm') {
        financial_price_nett_excl_vat_bpm = (+this.props.value || 0) / 1.21;
        financial_price_b2b_incl_vat_btpm = +financial_price_nett_excl_vat_bpm || 0;
      }

      if (type == 'financial_price_nett_excl_vat_bpm') {
        financial_price_b2b_incl_vat_btpm = +this.props.value || 0;
        financial_price_gross_incl_btw_bpm = (+financial_price_b2b_incl_vat_btpm || 0) * 1.21;
      }
    }

    // SCENARIO 3

    if (taxType == BtwOrMarge.BTW && vehiclePurpose == 'passenger' && isNew) {
      if (type == 'financial_price_b2b_incl_vat_btpm') {
        financial_price_gross_incl_btw_bpm = (+this.props.value || 0) - (+values?.financial_bpm_gross || 0);
        financial_price_nett_excl_vat_bpm = (+financial_price_gross_incl_btw_bpm || 0) / 1.21;
      }

      if (type == 'financial_price_gross_incl_btw_bpm') {
        financial_price_nett_excl_vat_bpm = (+this.props.value || 0) / 1.21;
        financial_price_b2b_incl_vat_btpm = (+financial_price_nett_excl_vat_bpm || 0) * 1.21 + +values?.financial_bpm_gross;
      }

      if (type == 'financial_price_nett_excl_vat_bpm') {
        financial_price_b2b_incl_vat_btpm = (+this.props.value || 0) * 1.21 + +values?.financial_bpm_gross;
        financial_price_gross_incl_btw_bpm = (+financial_price_b2b_incl_vat_btpm || 0) - +values?.financial_bpm_gross;
      }
    }

    // SCENARIO 4

    if (taxType == BtwOrMarge.MARGE && vehiclePurpose == 'passenger' && !isNew) {
      if (type == 'financial_price_b2b_incl_vat_btpm') {
        financial_price_gross_incl_btw_bpm = (+this.props.value || 0) - (+values?.outgoing_rest_bpm || 0);
        financial_price_nett_excl_vat_bpm = +financial_price_gross_incl_btw_bpm || 0;
      }

      if (type == 'financial_price_gross_incl_btw_bpm') {
        financial_price_nett_excl_vat_bpm = +this.props.value || 0;
        financial_price_b2b_incl_vat_btpm = (+financial_price_nett_excl_vat_bpm || 0) + +values?.outgoing_rest_bpm;
      }

      if (type == 'financial_price_nett_excl_vat_bpm') {
        financial_price_b2b_incl_vat_btpm = (+this.props.value || 0) + +values?.outgoing_rest_bpm;
        financial_price_gross_incl_btw_bpm = (+financial_price_b2b_incl_vat_btpm || 0) - +values?.outgoing_rest_bpm;
      }
    }

    // SCENARIO 5
    if (taxType == BtwOrMarge.MARGE && vehiclePurpose == 'commercial') {
      if (type == 'financial_price_b2b_incl_vat_btpm') {
        financial_price_gross_incl_btw_bpm = +this.props.value || 0;
        financial_price_nett_excl_vat_bpm = +financial_price_gross_incl_btw_bpm || 0;
      }

      if (type == 'financial_price_gross_incl_btw_bpm') {
        financial_price_nett_excl_vat_bpm = +this.props.value || 0;
        financial_price_b2b_incl_vat_btpm = +financial_price_nett_excl_vat_bpm || 0;
      }

      if (type == 'financial_price_nett_excl_vat_bpm') {
        financial_price_b2b_incl_vat_btpm = +this.props.value || 0;
        financial_price_gross_incl_btw_bpm = +financial_price_b2b_incl_vat_btpm || 0;
      }
    }

    // SCENARIO 6

    if (taxType == BtwOrMarge.MARGE && vehiclePurpose == 'passenger' && isNew) {
      if (type == 'financial_price_b2b_incl_vat_btpm') {
        financial_price_gross_incl_btw_bpm = (+this.props.value || 0) - +values?.financial_bpm_gross;
        financial_price_nett_excl_vat_bpm = +financial_price_gross_incl_btw_bpm || 0;
      }

      if (type == 'financial_price_gross_incl_btw_bpm') {
        financial_price_nett_excl_vat_bpm = +this.props.value || 0;
        financial_price_b2b_incl_vat_btpm = (+financial_price_nett_excl_vat_bpm || 0) + +values?.financial_bpm_gross;
      }

      if (type == 'financial_price_nett_excl_vat_bpm') {
        financial_price_b2b_incl_vat_btpm = (+this.props.value || 0) + +values?.financial_bpm_gross;
        financial_price_gross_incl_btw_bpm = (+financial_price_b2b_incl_vat_btpm || 0) - +values?.financial_bpm_gross;
      }
    }

    // SCENARIO 7
    if (taxType == BtwOrMarge.BTW && vehiclePurpose == 'commercial' && !!restBPMApplicableForCommercial) {
      if (type == 'financial_price_b2b_incl_vat_btpm') {
        financial_price_gross_incl_btw_bpm = ((+this.props.value || 0) - +values?.outgoing_rest_bpm) * 1.21;
        financial_price_nett_excl_vat_bpm = (+this.props.value || 0) - +values?.outgoing_rest_bpm;
      }

      if (type == 'financial_price_gross_incl_btw_bpm') {
        financial_price_nett_excl_vat_bpm = (+this.props.value || 0) / 1.21;
        financial_price_b2b_incl_vat_btpm = financial_price_nett_excl_vat_bpm + +values?.outgoing_rest_bpm;
      }

      if (type == 'financial_price_nett_excl_vat_bpm') {
        financial_price_b2b_incl_vat_btpm = (+this.props.value || 0) + +values?.outgoing_rest_bpm;
        financial_price_gross_incl_btw_bpm = (+this.props.value || 0) * 1.21;
      }
    }

    this.props.setValue(fixPriceFormat(financial_price_b2b_incl_vat_btpm), 'financial_price_b2b_incl_vat_btpm');
    this.props.setValue(fixPriceFormat(financial_price_gross_incl_btw_bpm), 'financial_price_gross_incl_btw_bpm');
    this.props.setValue(fixPriceFormat(financial_price_nett_excl_vat_bpm), 'financial_price_nett_excl_vat_bpm');

    return;
  }

  applyDisableInput() {
    return !this.isWientjes;
  }

  render() {
    if (
      this.props.serverConfig.disableBPM &&
      env.clientId != AppClientId.AUTOWIENTJES &&
      env.clientId != AppClientId.AUTOHUISSALLAND &&
      ['rest_bpm', 'financial_bpm_gross', 'outgoing_rest_bpm'].includes(this.props.type)
    )
      return '';
    if (this.props.work) return <Loader small />;
    return <Container>{this.wrap(this.renderField())}</Container>;
  }

  renderField() {
    if (this.props.value === undefined) return null;
    if (this.isCheckbox()) {
      return (
        <CheckboxComponent
          className="checkbox"
          label={this.lbl(`vehicle.property.${this.props.type}`)}
          checked={this.props.value === '1'}
          onChange={() => {
            const value = this.props.value === '1' ? '0' : '1';
            return this.props.setValue(value);
          }}
        />
      );
    }

    if (this.isDropbox()) {
      return this.dropdown();
    }

    if (this.isManualDropbox()) {
      return this.manualDropdown();
    }

    switch (this.props.type) {
      case 'financial_purchase_price_marge_bpm':
        return (
          <div className={styles.buttonLabel}>
            <div className={styles.labelCompany}>
              <input
                className="form-control"
                type="text"
                key={this.props.type}
                disabled={this.props.vehicle?.incoming_invoice}
                value={this.props.value}
                onChange={e => this.onChangeInputPurchasePrice(e, this.props.type)}
              />
            </div>
            <div className={styles.decoratorLabel}>€</div>
          </div>
        );
      case 'financial_purchase_price_marge_excl_bpm':
        return (
          <div className={styles.buttonLabel}>
            <div className={styles.labelCompany}>
              <input
                className="form-control"
                type="text"
                disabled={this.applyDisableInput()}
                key={this.props.type}
                value={this.props.value}
                onChange={e => this.onChangeInputPurchasePrice(e, this.props.type)}
              />
            </div>
            <div className={styles.decoratorLabel}>€</div>
          </div>
        );
      case 'financial_purchase_price_incl_vat_bpm':
        return (
          <div className={styles.buttonLabel}>
            <div className={styles.labelCompany}>
              <input
                className="form-control"
                type="text"
                key={this.props.type}
                disabled={this.props.vehicle?.incoming_invoice}
                value={this.props.value}
                onChange={e => this.onChangeInputPurchasePrice(e, this.props.type)}
              />
            </div>
            <div className={styles.decoratorLabel}>€</div>
          </div>
        );

      case 'financial_purchase_price_incl_vat_excl_bpm':
        return (
          <div className={styles.buttonLabel}>
            <div className={styles.labelCompany}>
              <input
                className="form-control"
                type="text"
                disabled={this.applyDisableInput()}
                key={this.props.type}
                value={this.props.value}
                onChange={e => this.onChangeInputPurchasePrice(e, this.props.type)}
              />
            </div>
            <div className={styles.decoratorLabel}>€</div>
          </div>
        );
      case 'rest_bpm':
        return (
          <div className={styles.buttonLabel}>
            <div className={styles.labelCompany}>
              <input
                className="form-control"
                type="text"
                key={this.props.type}
                disabled={this.props.vehicle?.incoming_invoice}
                value={this.props.value}
                onChange={e => this.onChangeInputPurchasePrice(e, this.props.type)}
              />
            </div>
            {this.isWientjes ? (
              <div className={styles.decoratorLabel}>€</div>
            ) : (
              <ButtonComponent disabled={this.props.vehicle?.incoming_invoice} style="ButtonSoftPrimary">
                {this.lbl('vehicle.create.vin.calculate')}
              </ButtonComponent>
            )}
          </div>
        );
      case 'financial_bpm_gross':
        return (
          <Input
            type="text"
            key={this.props.type}
            value={this.props.value}
            onChange={evt => this.onChangePriceWithRounding(evt.target.value)}

          />
        );
      case 'economic_prize':
        return (
          this.isWientjes && (
            <div className={styles.buttonLabel}>
              <div className={styles.labelCompany}>
                <input
                  className="form-control"
                  type="text"
                  key={this.props.type}
                  value={this.props.value}
                  onChange={evt => this.onChangePriceWithRounding(evt.target.value)}
                />
              </div>
              <div className={styles.decoratorLabel}>€</div>
            </div>
          )
        );

      case 'vat_amount':
        return (
          <div className={styles.buttonLabel}>
            <div className={styles.labelCompany}>
              <input
                className="form-control"
                type="text"
                disabled={true}
                key={this.props.type}
                value={this.props.value}
                onChange={evt => this.onChangePriceWithRounding(evt.target.value)}
              />
            </div>
            <div className={styles.decoratorLabel}>€</div>
          </div>
        );
      case 'vat_percentage':
        return (
          <div className={styles.buttonLabel}>
            <div className={styles.labelCompany}>
              <input
                className="form-control"
                type="text"
                key={this.props.type}
                disabled={true}
                value={this.props.value}
                onChange={evt => this.onChangePriceWithRounding(evt.target.value)}
              />
            </div>
            <div className={styles.decoratorLabel}>%</div>
          </div>
        );
      case 'financial_purchase_price_excl_vat_excl_bpm':
        return (
          <div className={styles.buttonLabel}>
            <div className={styles.labelCompany}>
              <input
                className="form-control"
                type="text"
                disabled={this.applyDisableInput()}
                key={this.props.type}
                value={this.props.value}
                onChange={e => this.onChangeInputPurchasePrice(e, this.props.type)}
              />
            </div>
            <div className={styles.decoratorLabel}>€</div>
          </div>
        );
      case 'financial_price_b2c':
        return (
          <div className={styles.buttonLabel}>
            <div className={styles.labelCompany}>
              <input
                className="form-control"
                type="text"
                key={this.props.type}
                value={this.props.value}
                onChange={evt => this.onChangePriceWithRounding(evt.target.value)}
              />
            </div>
            <div className={styles.buttonContainer}>
              <div className={styles.decoratorLabel}>€</div>

              <ButtonComponent
                style="ButtonSoftDelete"
                disabled={this.props.disabled}
                onClick={() => {
                  this.props.deleteProperty(this.props.values?.id, this.props.type, result => {
                    if (result.length) {
                      this.props.setValue('');
                    }
                  });
                }}
              >
                <CircleTimes />
              </ButtonComponent>
            </div>
          </div>
        );
      case 'financial_price_b2b_incl_vat_btpm':
        return (
          <div className={styles.buttonsLabel}>
            <div className={styles.labelCompany}>
              <input
                className="form-control"
                type="text"
                key={this.props.type}
                value={this.props.value}
                onChange={evt => this.onChangePriceWithRounding(evt.target.value)}
              />
            </div>
            <>{this.salesPriceDecorator()}</>
          </div>
        );
      case 'financial_price_gross_incl_btw_bpm':
        return (
          <div className={styles.buttonsLabel}>
            <div className={styles.labelCompany}>
              <input
                className="form-control"
                type="text"
                key={this.props.type}
                value={this.props.value}
                onChange={evt => this.onChangePriceWithRounding(evt.target.value)}
              />
            </div>
            <>{this.salesPriceDecorator()}</>
          </div>
        );
      case 'financial_price_nett_excl_vat_bpm':
        return (
          <div className={styles.buttonsLabel}>
            <div className={styles.labelCompany}>
              <input
                className="form-control"
                type="text"
                key={this.props.type}
                value={this.props.value}
                onChange={evt => this.onChangePriceWithRounding(evt.target.value)}
              />
            </div>
            <>{this.salesPriceDecorator()}</>
          </div>
        );
      case 'buying_company':
        return (
          <VehicleCreateFieldCompany
            title={this.props.type}
            key={this.props.type}
            type={this.props.type}
            onChange={value => this.props.setValue(value)}
          />
        );
        break;
      case 'buying_contact':
        return (
          <VehicleCreateFieldCompanyUser
            title={this.props.type}
            companyId={this.props.values.buying_company}
            key={`${this.props.type}-${this.props.values.buying_company}`}
            type={this.props.type}
            onChange={value => this.props.setValue(value)}
          />
        );
        break;
      case 'outgoing_rest_bpm':
        return (
          <div className={styles.buttonLabel}>
            <div className={styles.labelCompany}>
              <input
                className="form-control"
                type="text"
                key={this.props.type}
                disabled={this.props.vehicle?.outgoing_invoice}
                value={this.props.value}
                onChange={e => this.onChangeInputPurchasePrice(e, this.props.type)}
              />
            </div>
            <div className={styles.decoratorLabel}>€</div>
          </div>
        );
        break;
      case 'financial_purchase_price_incl_bpm_excl_btw':
        return (
          <div className={styles.buttonLabel}>
            <div className={styles.labelCompany}>
              <input
                className="form-control"
                type="text"
                disabled={this.applyDisableInput()}
                key={this.props.type}
                value={this.props.value}
                onChange={e => this.onChangeInputPurchasePrice(e, this.props.type)}
              />
            </div>
            <div className={styles.decoratorLabel}>€</div>
          </div>
        );
    }
  }
}

VehicleCreateFieldComp.propTypes = {
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) =>
  createStructuredSelector({
    work: selectWork(REQUEST_VEHICLE_PROPERTY_UPDATE),
    value: selectNewVehicleValue(ownProps.type),
    values: selectNewVehicle(),
    vehicle: selectVehicle(ownProps.vehicle_id),
    taxType: selectNewVehicleValue('tax_type'),
    financial_purchase_price_incl_vat_bpm: selectNewVehicleValue('financial_purchase_price_incl_vat_bpm'),
    financial_purchase_price_marge_bpm: selectNewVehicleValue('financial_purchase_price_marge_bpm'),
    financial_purchase_price_incl_vat_excl_bpm: selectNewVehicleValue('financial_purchase_price_incl_vat_excl_bpm'),
    financial_purchase_price_excl_vat_excl_bpm: selectNewVehicleValue('financial_purchase_price_excl_vat_excl_bpm'),
    financial_purchase_price_incl_bpm_excl_btw: selectNewVehicleValue('financial_purchase_price_incl_bpm_excl_btw'),
    financial_purchase_price_marge_excl_bpm: selectNewVehicleValue('financial_purchase_price_marge_excl_bpm'),
    rest_bpm: selectNewVehicleValue('rest_bpm'),
    financial_bpm_gross: selectNewVehicleValue('financial_bpm_gross'),
    vehicle_purpose: selectNewVehicleValue('vehicle_purpose'),
    serverConfig: selectConfig(),
  });

function mapDispatchToProps(dispatch, ownProps) {
  return {
    dispatch,
    setValue(value, name = null) {
      name = name ?? ownProps.type;
      dispatch(setNewVehicleValue(name, value));
    },
    deleteProperty(vehicleId, propertyName, callback) {
      dispatch(deleteVehicleProperty(vehicleId, propertyName, callback));
    },
    initiateValue() {
      dispatch(initiateNewVehicleValue(ownProps.type));
    },
    calculatePurchasePricesBTW(enteredAmounts, vehiclePurpose) {
      dispatch(calculatePurchasePricesBTW(ownProps.vehicle_id, enteredAmounts, vehiclePurpose));
    },
    calculatePurchasePricesMARGE(enteredAmounts, vehiclePurpose) {
      dispatch(calculatePurchasePricesMARGE(ownProps.vehicle_id, enteredAmounts, vehiclePurpose));
    },
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export const VehicleCreateField = compose(withConnect, withRouter)(VehicleCreateFieldComp);
